import React, { useState } from 'react';
import {
  LayoutDashboard, AlertCircle, ClipboardList, Calendar, Wrench, Package,
  TrendingUp, Bell, FileText, Building2, Shield, Users, ClipboardCheck,
  Clock, BarChart3, KeyRound, Search, CheckCircle
} from 'lucide-react';

/**
 * Role-based dashboard preview tabs for the landing page.
 * Mock cards that visually represent what each role sees after login.
 * No real data — purely illustrative for marketing purposes.
 */

const ROLES = [
  {
    key: 'manager',
    label: 'Manager',
    title: 'Operations Manager',
    intro: 'Approve material requests, monitor SLAs, review monthly metrics, supervise field teams.',
    color: 'from-emerald-500 to-teal-500',
    kpis: [
      { icon: AlertCircle, label: 'Open Complaints', value: '12', tone: 'amber' },
      { icon: Wrench, label: 'PPM Due', value: '8', tone: 'orange' },
      { icon: ClipboardCheck, label: 'Approvals', value: '5', tone: 'blue' },
      { icon: Package, label: 'Low Stock', value: '3', tone: 'red' },
    ],
    actions: ['Approve Materials', 'Assign Tasks', 'View Analytics', 'Export Reports']
  },
  {
    key: 'tenant',
    label: 'Tenant',
    title: 'Tenant / Resident',
    intro: 'Raise complaints, message FM, see notices, sign service reports, view emergency contacts.',
    color: 'from-cyan-500 to-sky-500',
    kpis: [
      { icon: AlertCircle, label: 'My Complaints', value: '2', tone: 'amber' },
      { icon: Bell, label: 'Notices', value: '4', tone: 'blue' },
      { icon: ClipboardCheck, label: 'Awaiting Sign', value: '1', tone: 'purple' },
      { icon: Shield, label: 'Emergency', value: '997', tone: 'red' },
    ],
    actions: ['New Complaint', 'View Notices', 'Sign Service Report', 'Emergency Contacts']
  },
  {
    key: 'security',
    label: 'Security',
    title: 'Security Supervisor',
    intro: 'Daily routines, incident reports, visitor records, emergency response, attendance check-in.',
    color: 'from-rose-500 to-red-500',
    kpis: [
      { icon: Calendar, label: "Today's Routines", value: '6', tone: 'blue' },
      { icon: AlertCircle, label: 'Incidents', value: '0', tone: 'green' },
      { icon: ClipboardCheck, label: 'Check-ins', value: '14', tone: 'teal' },
      { icon: Bell, label: 'Alerts', value: '1', tone: 'amber' },
    ],
    actions: ['Start Patrol', 'Log Incident', 'Visitor Entry', 'Emergency Contacts']
  },
  {
    key: 'engineer',
    label: 'Engineer',
    title: 'Supervisor / Engineer',
    intro: 'Manage PPM, asset audits, AMC scope tracking, service-provider KPIs, inspection schedules.',
    color: 'from-blue-500 to-indigo-500',
    kpis: [
      { icon: Wrench, label: 'PPM Tasks', value: '24', tone: 'orange' },
      { icon: Building2, label: 'Buildings', value: '8', tone: 'teal' },
      { icon: BarChart3, label: 'AMC % SLA', value: '92%', tone: 'green' },
      { icon: Clock, label: 'Overdue', value: '2', tone: 'red' },
    ],
    actions: ['Add AMC Scope', 'Asset Audit', 'PPM Schedule', 'Provider KPIs']
  },
  {
    key: 'technician',
    label: 'Technician',
    title: 'Field Technician',
    intro: 'See your assignments, scan asset QR, submit service reports with materials, capture tenant signatures.',
    color: 'from-amber-500 to-orange-500',
    kpis: [
      { icon: ClipboardList, label: 'My Jobs', value: '4', tone: 'blue' },
      { icon: Wrench, label: 'PPM Due', value: '3', tone: 'orange' },
      { icon: FileText, label: 'Reports', value: '12', tone: 'teal' },
      { icon: KeyRound, label: 'Hours Today', value: '6.5', tone: 'purple' },
    ],
    actions: ['Scan Asset QR', 'Open Complaint', 'Submit Report', 'Request Materials']
  },
  {
    key: 'cs',
    label: 'Customer Service',
    title: 'CS Supervisor',
    intro: 'Triage tenant complaints, assign technicians, post notices, monitor resolution SLA.',
    color: 'from-purple-500 to-fuchsia-500',
    kpis: [
      { icon: AlertCircle, label: 'Unassigned', value: '5', tone: 'amber' },
      { icon: Users, label: 'Active Cases', value: '18', tone: 'blue' },
      { icon: TrendingUp, label: 'CSAT (avg)', value: '4.6', tone: 'green' },
      { icon: Bell, label: 'Notices Posted', value: '2', tone: 'teal' },
    ],
    actions: ['Triage Queue', 'Post Notice', 'Tenant Directory', 'Resolution Trends']
  },
  {
    key: 'staff',
    label: 'Staff',
    title: 'General Staff',
    intro: 'Daily check-in/out, schedules, internal notices, contacts directory, self-service profile.',
    color: 'from-slate-500 to-slate-600',
    kpis: [
      { icon: ClipboardCheck, label: 'Attendance', value: 'IN', tone: 'green' },
      { icon: Calendar, label: 'My Tasks', value: '7', tone: 'blue' },
      { icon: Bell, label: 'Notices', value: '3', tone: 'amber' },
      { icon: Search, label: 'Contacts', value: '42', tone: 'teal' },
    ],
    actions: ['Check In/Out', "Today's Schedule", 'Read Notices', 'Find Contact']
  },
];

const TONE_BG = {
  amber: 'bg-amber-500/15 text-amber-300',
  orange: 'bg-orange-500/15 text-orange-300',
  blue: 'bg-blue-500/15 text-blue-300',
  red: 'bg-red-500/15 text-red-300',
  green: 'bg-emerald-500/15 text-emerald-300',
  teal: 'bg-teal-500/15 text-teal-300',
  purple: 'bg-purple-500/15 text-purple-300',
};

export default function RoleDashboardPreview() {
  const [active, setActive] = useState('manager');
  const role = ROLES.find(r => r.key === active);

  return (
    <section id="role-views" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-24">
      <div className="text-center mb-10">
        <div className="text-xs text-teal-400 font-semibold uppercase tracking-[0.2em] mb-3">Role-Based Views</div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
          Every role sees only <span className="bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent">what matters to them.</span>
        </h2>
        <p className="text-base text-slate-400 max-w-2xl mx-auto">
          A focused dashboard for every role in your FM organization — preview what each user sees the moment they sign in.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-8" role="tablist" data-testid="role-tabs">
        {ROLES.map(r => (
          <button
            key={r.key}
            data-testid={`role-tab-${r.key}`}
            role="tab"
            aria-selected={active === r.key}
            onClick={() => setActive(r.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              active === r.key
                ? 'bg-gradient-to-r ' + r.color + ' text-white shadow-lg'
                : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700 border border-slate-700'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Active view card — mock dashboard */}
      <div data-testid={`role-preview-${active}`} className="relative rounded-3xl overflow-hidden border border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950 shadow-2xl">
        {/* Mock browser chrome */}
        <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/80 flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" />
          </div>
          <div className="flex-1 text-center text-xs text-muted-foreground font-mono">fm-flexi-pro.app/dashboard</div>
          <div className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r ${role.color} text-white`}>
            {role.label}
          </div>
        </div>

        <div className="p-6 lg:p-8">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-white">Welcome back, {role.title}</h3>
            <p className="text-sm text-slate-400 mt-1">{role.intro}</p>
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {role.kpis.map((k, i) => {
              const Icon = k.icon;
              return (
                <div key={i} className="p-4 rounded-xl bg-slate-800/40 border border-slate-700 hover:border-teal-500/50 transition">
                  <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg mb-2 ${TONE_BG[k.tone] || 'bg-slate-700 text-slate-300'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="text-2xl font-bold text-white">{k.value}</div>
                  <div className="text-xs text-muted-foreground">{k.label}</div>
                </div>
              );
            })}
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {role.actions.map((a, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-slate-800/40 border border-slate-700">
                <CheckCircle className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
                <span className="text-xs text-slate-300 truncate">{a}</span>
              </div>
            ))}
          </div>

          {/* Note */}
          <div className="mt-6 text-[11px] text-slate-500 italic text-center">
            * Illustrative preview. Real dashboards are populated from your live operations data.
          </div>
        </div>
      </div>
    </section>
  );
}
